import { motion } from "framer-motion";
import { Check, ArrowUpRight, Percent } from "lucide-react";
import { Reveal, SectionTag, EASE } from "@/components/Reveal";

const included = [
  "High-pressure drain jetting",
  "CCTV camera inspection",
  "Locating equipment where required",
  "Materials quoted separately before we proceed",
];

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="border-b-2 border-white/10 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionTag number="01" label="How The Pricing Works" color="#adff01" />

        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-display text-4xl uppercase leading-[0.9] tracking-tight sm:text-6xl">
                No smoke.
                <br />
                No mirrors.
                <br />
                <span className="text-[#adff01]">Just the price.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-300 md:text-lg">
              <p data-testid="pricing-copy">
                <span className="font-bold text-[#adff01]">Blocked drains, Perth plumbing, upfront! </span>
                Our blocked drain service is{" "}
                <span className="font-bold text-white">$360 for the first half-hour</span>,
                which includes a <span className="font-bold text-white">$200 specialist
                equipment fee</span>. This gives our plumber access to the equipment
                needed to properly investigate and clear the blockage — drain jetting,
                CCTV camera inspection and locating equipment where required. After the
                first half-hour, it's <span className="font-bold text-white">$45 per 15 minute
                increments</span>. 90% of blockages are resolved within the first hour.
              </p>
              <p className="mt-4 border-l-2 border-[#169ed9] pl-4 text-sm text-zinc-400">
                Please note: the specialist equipment fee applies once the equipment is
                allocated to your job, even if every item of equipment is not used onsite.
              </p>
            </Reveal>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {included.map((item, i) => (
                <Reveal key={item} delay={0.15 + i * 0.08}>
                  <li className="flex items-start gap-3 border-2 border-white/10 bg-[#121214] p-4 text-sm font-bold text-zinc-200">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#adff01]" strokeWidth={3} />
                    {item}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-6 lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: EASE }}
              whileHover={{ x: -4, y: -4 }}
              className="shadow-hard-green border-2 border-[#adff01] bg-[#121214] p-8 transition-shadow hover:shadow-[4px_4px_0_0_#adff01] md:p-10"
              data-testid="pricing-first-hour-card"
            >
              <p className="text-xs font-extrabold uppercase tracking-[0.3em] text-zinc-400">
                First half-hour — all in
              </p>
              <p className="mt-3 font-display text-7xl tracking-tight text-[#adff01] md:text-8xl">
                $360
              </p>
              <p className="mt-3 text-sm font-bold text-zinc-300">
                Includes $200 specialist equipment fee. GST included.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 44 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
              className="grid grid-cols-2 gap-6"
            >
              <div
                className="border-2 border-white/15 bg-[#121214] p-6"
                data-testid="pricing-increment-card"
              >
                <p className="font-display text-3xl text-white md:text-4xl">$45</p>
                <p className="mt-2 text-xs font-bold uppercase tracking-wider text-zinc-400">
                  per 15 min after the first half-hour
                </p>
              </div>
              <div
                className="shadow-hard-azure border-2 border-[#169ed9] bg-[#121214] p-6"
                data-testid="pricing-stat-card"
              >
                <p className="flex items-start font-display text-3xl text-[#169ed9] md:text-4xl">
                  90<Percent className="h-5 w-5" strokeWidth={3} />
                </p>
                <p className="mt-2 text-xs font-bold uppercase tracking-wider text-zinc-400">
                  cleared within the first hour
                </p>
              </div>
            </motion.div>

            <Reveal delay={0.2}>
              <motion.a
                href="#book-it-in"
                data-testid="pricing-book-button"
                whileHover={{ x: -3, y: -3 }}
                className="shadow-hard-green flex items-center justify-center gap-2 border-2 border-black bg-[#ff66c4] px-6 py-4 font-display text-sm uppercase tracking-wide text-black transition-shadow hover:shadow-[4px_4px_0_0_#adff01]"
              >
                Lock In This Price
                <ArrowUpRight className="h-5 w-5" strokeWidth={3} />
              </motion.a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
